import { useId, useState } from 'react';
import type { Service } from '@salonomy/types';
import { Button, Field, Modal, TextInput, Toggle } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { useCreateService, useUpdateService } from '../../data/services.js';
import { useToast } from '../../lib/toast.js';
import { penceToPounds, poundsToPence } from './poundsPence.js';
import { ServicePricingMatrixModal } from './ServicePricingMatrixModal.js';

/**
 * Create / edit a service (WS6). The price lives in its own grouped block ("Pricing") so the
 * brief-09 pricing variants (per-stylist, peak/off-peak, deposits) can slot in beside `priceFrom`
 * without a form rebuild. Money is integer pence end-to-end; the £ input is a display convenience.
 */
export function ServiceModal({
  existing,
  categories,
  onClose,
}: {
  existing?: Service;
  categories: string[];
  onClose: () => void;
}) {
  const create = useCreateService();
  const update = useUpdateService();
  const toast = useToast();
  const editing = Boolean(existing);
  const datalistId = useId();

  const [name, setName] = useState(existing?.name ?? '');
  const [category, setCategory] = useState(existing?.category ?? '');
  const [durationMin, setDurationMin] = useState(String(existing?.durationMin ?? 30));
  const [processingMin, setProcessingMin] = useState(String(existing?.processingMin ?? 0));
  const [gapMin, setGapMin] = useState(String(existing?.gapMin ?? 0));
  const [priceFrom, setPriceFrom] = useState(penceToPounds(existing?.priceFrom ?? null));
  const [requiresPatchTest, setRequiresPatchTest] = useState(existing?.requiresPatchTest ?? false);
  const [patchTestValidDays, setPatchTestValidDays] = useState(
    existing?.patchTestValidDays != null ? String(existing.patchTestValidDays) : '180',
  );
  const [onlineBookable, setOnlineBookable] = useState(existing?.onlineBookable ?? true);
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [pricingMatrix, setPricingMatrix] = useState(false);

  async function archive() {
    if (!existing) return;
    setBusy(true);
    try {
      await update.mutateAsync({ id: existing.id, body: { active: false } });
      toast.success(`${existing.name} archived`);
      setConfirmArchive(false);
      onClose();
    } catch {
      toast.error('Could not archive this service.');
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!name.trim()) {
      toast.error('A service name is required.');
      return;
    }
    const duration = Number(durationMin);
    if (!Number.isInteger(duration) || duration < 5 || duration > 600) {
      toast.error('Duration must be 5–600 minutes.');
      return;
    }
    const price = poundsToPence(priceFrom);
    if (price == null) {
      toast.error('Enter a valid price.');
      return;
    }
    setBusy(true);
    const body = {
      name: name.trim(),
      category: category.trim() || null,
      durationMin: duration,
      processingMin: Number(processingMin) || 0,
      gapMin: Number(gapMin) || 0,
      priceFrom: price,
      requiresPatchTest,
      patchTestValidDays: requiresPatchTest ? Number(patchTestValidDays) || null : null,
      onlineBookable,
    };
    try {
      if (editing && existing) {
        await update.mutateAsync({ id: existing.id, body });
        toast.success('Service updated');
      } else {
        await create.mutateAsync(body);
        toast.success('Service added');
      }
      onClose();
    } catch {
      toast.error('Could not save the service.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={editing ? 'Edit service' : 'Add service'}
        width={520}
        footer={
          <>
            {editing && existing?.active ? (
              <Button
                variant="ghost"
                onClick={() => setConfirmArchive(true)}
                disabled={busy}
                style={{ marginRight: 'auto', color: 'var(--slm-danger)' }}
              >
                Archive
              </Button>
            ) : null}
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy}>
              {editing ? 'Save' : 'Add service'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
          <Field label="Name">
            <TextInput
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Cut & finish"
            />
          </Field>
          <Field label="Category" hint="Free text — reuse one below.">
            <TextInput
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              list={datalistId}
              placeholder="e.g. Colour"
            />
            <datalist id={datalistId}>
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
        </div>

        <div className="section-title" style={{ fontSize: 15, margin: '8px 0 2px' }}>
          Timing
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <Field label="Duration (min)" hint="Chair time">
            <TextInput
              type="number"
              inputMode="numeric"
              min={5}
              max={600}
              value={durationMin}
              onChange={(e) => setDurationMin(e.target.value)}
            />
          </Field>
          <Field label="Processing (min)" hint="Hands-off">
            <TextInput
              type="number"
              inputMode="numeric"
              min={0}
              max={240}
              value={processingMin}
              onChange={(e) => setProcessingMin(e.target.value)}
            />
          </Field>
          <Field label="Gap (min)" hint="Clean-down">
            <TextInput
              type="number"
              inputMode="numeric"
              min={0}
              max={120}
              value={gapMin}
              onChange={(e) => setGapMin(e.target.value)}
            />
          </Field>
        </div>

        {/* Pricing — its own group; brief-09 variants/levels live in the matrix modal below. */}
        <div className="section-title" style={{ fontSize: 15, margin: '8px 0 2px' }}>
          Pricing
        </div>
        <Field
          label="Base price (£)"
          hint="The single price, and the base for any variants/levels. Clients see this as “from £X”."
        >
          <TextInput
            inputMode="decimal"
            value={priceFrom}
            onChange={(e) => setPriceFrom(e.target.value)}
            placeholder="0.00"
          />
        </Field>
        {/* WS9 — variant (length/option) + per-level pricing matrix. Nested under a saved service,
            so it only opens once the service exists; for a new service, save first. */}
        {editing && existing ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPricingMatrix(true)}
            style={{ marginTop: 8 }}
          >
            Pricing variants &amp; levels…
          </Button>
        ) : (
          <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>
            Save the service to add length variants (short/medium/long) and price by stylist level.
          </div>
        )}

        <div className="section-title" style={{ fontSize: 15, margin: '8px 0 2px' }}>
          Booking
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Toggle checked={onlineBookable} onChange={setOnlineBookable} label="Bookable online" />
          <Toggle
            checked={requiresPatchTest}
            onChange={setRequiresPatchTest}
            label="Requires patch test"
          />
        </div>
        {requiresPatchTest ? (
          <Field label="Patch test valid for (days)" hint="How long a passed patch test counts.">
            <TextInput
              type="number"
              inputMode="numeric"
              min={1}
              max={3650}
              value={patchTestValidDays}
              onChange={(e) => setPatchTestValidDays(e.target.value)}
            />
          </Field>
        ) : null}
      </Modal>

      <ConfirmModal
        open={confirmArchive}
        title="Archive service"
        confirmLabel="Archive service"
        confirmPhrase={existing?.name}
        busy={busy}
        onConfirm={archive}
        onClose={() => setConfirmArchive(false)}
      >
        <p style={{ margin: '0 0 8px' }}>
          Archiving <strong>{existing?.name}</strong> removes it from new bookings and the online
          booking page straight away. It stays on past appointments that already used it, and any
          per-service booking policy attached to it is kept.
        </p>
        <p style={{ margin: 0 }}>You can bring it back later by editing it and re-enabling it.</p>
      </ConfirmModal>

      {pricingMatrix && existing ? (
        <ServicePricingMatrixModal service={existing} onClose={() => setPricingMatrix(false)} />
      ) : null}
    </>
  );
}
